import React, { useCallback, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { formatDate } from '@/utils/dateFormat'
import { useCompany } from '@/hooks/useCompany'
import { deactivateCompany, updateCompany } from '@/api/companies'
import { getProjectsForCompany } from '@/api/projects'
import { Company, Project } from '@/types/index'
import { DataTable } from '@/components/ui/DataTable'
import { Modal } from '@/components/ui/Modal'
import { AuditHistoryTab } from '@/components/ui/AuditHistoryTab'
import { ChevronLeft, AlertCircle } from 'lucide-react'
import { useAuth } from '@/auth/useAuth'
import { UserRole } from '@/types'

// Bug #331: `lab_company` has no `status` column — the model carries
// `is_active` only, and the mock hook had been supplying `status: 'Active'`,
// which hid that. Wiring the real API up naively would have produced a blank
// badge, so status is derived here rather than read.
const statusLabel = (isActive: boolean): string => (isActive ? 'Active' : 'Inactive')

const titleCase = (value?: string): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : '-'

const isActiveStatus = (value?: string): boolean =>
  (value || '').toLowerCase() === 'active'

const backButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  color: 'var(--color-magenta)',
  fontSize: '14px',
  fontWeight: 500,
  marginBottom: '20px',
  padding: 0,
  fontFamily: 'var(--font-family)',
}

const badgeStyle = (active: boolean): React.CSSProperties => ({
  display: 'inline-block',
  padding: '4px 10px',
  borderRadius: '4px',
  fontSize: '12px',
  fontWeight: 600,
  backgroundColor: active ? '#dbeafe' : '#f3f4f6',
  color: active ? '#0284c7' : '#6b7280',
})

const CompanyDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { company, loading, error, refetch } = useCompany(id || '')
  // Bug #370: companies are integration-fed and edit / deactivate are
  // platform-admin only on the backend, so only platform admins see the buttons.
  const { user } = useAuth()
  const isPlatformAdmin = !!user?.roles?.includes(UserRole.PlatformAdmin)
  const [activeTab, setActiveTab] = useState<'overview' | 'projects' | 'audit'>('overview')

  // Bug #331: the "Associated projects" tab used to hard-code two invented
  // projects ("Platform Upgrade", "Data Migration") on every company, and
  // clicking one navigated to /projects/1, which does not exist. That is
  // worse than an empty tab, because it reads as real. The backend already
  // exposes `client_id` as a filter on GET /projects, so this is a fetch
  // rather than new backend work.
  const [projects, setProjects] = useState<Project[]>([])
  const [projectsLoading, setProjectsLoading] = useState(false)
  const [projectsError, setProjectsError] = useState<string | null>(null)

  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editData, setEditData] = useState<Partial<Company>>({})
  const [saving, setSaving] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  useEffect(() => {
    if (activeTab !== 'projects' || !id) return
    let cancelled = false
    setProjectsLoading(true)
    setProjectsError(null)
    getProjectsForCompany(id)
      .then((items) => {
        if (!cancelled) setProjects(items)
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setProjectsError(
            err instanceof Error ? err.message : 'Failed to load associated projects'
          )
        }
      })
      .finally(() => {
        if (!cancelled) setProjectsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [activeTab, id])

  const handleSave = useCallback(async () => {
    if (!id) return
    setSaving(true)
    setActionError(null)
    try {
      await updateCompany(id, {
        name: editData.name,
        domain: editData.domain,
        sourceUrl: editData.sourceUrl,
      })
      setEditModalOpen(false)
      await refetch()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to save company')
    } finally {
      setSaving(false)
    }
  }, [editData, id, refetch])

  const handleDeactivate = useCallback(async () => {
    if (!id) return
    setActionError(null)
    try {
      await deactivateCompany(id)
      // The list endpoint filters out inactive companies, so there is
      // nothing useful left on this page once it succeeds.
      navigate('/companies')
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to deactivate company')
    }
  }, [id, navigate])

  if (loading) {
    return (
      <div style={{ padding: '24px' }}>
        <button onClick={() => navigate('/companies')} style={backButtonStyle}>
          <ChevronLeft size={18} />
          Back to companies
        </button>
        <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-dark-grey)' }}>
          Loading company...
        </div>
      </div>
    )
  }

  if (error || !company) {
    return (
      <div style={{ padding: '24px' }}>
        <button onClick={() => navigate('/companies')} style={backButtonStyle}>
          <ChevronLeft size={18} />
          Back to companies
        </button>
        <div
          style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            padding: '16px',
            display: 'flex',
            gap: '10px',
          }}
        >
          <AlertCircle size={20} style={{ color: '#dc2626', flexShrink: 0 }} />
          <div>
            <p style={{ margin: 0, color: '#dc2626', fontWeight: 500 }}>Company not found</p>
            <p style={{ margin: '4px 0 0 0', color: '#991b1b', fontSize: '13px' }}>
              {error || 'Unable to load company details'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: '0' }}>
      <div style={{ padding: '24px', paddingBottom: '16px' }}>
        <button onClick={() => navigate('/companies')} style={backButtonStyle}>
          <ChevronLeft size={18} />
          Back to companies
        </button>

        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '20px',
          }}
        >
          <div>
            <h1
              style={{
                fontSize: '28px',
                fontWeight: 700,
                margin: '0 0 8px 0',
                color: 'var(--color-black)',
              }}
            >
              {company.name}
            </h1>
            <span style={badgeStyle(company.isActive)}>{statusLabel(company.isActive)}</span>
          </div>
          {isPlatformAdmin && (
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => {
                  setEditData(company)
                  setActionError(null)
                  setEditModalOpen(true)
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--color-magenta)',
                  color: 'var(--color-white)',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family)',
                }}
              >
                Edit
              </button>
              <button
                onClick={handleDeactivate}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family)',
                }}
              >
                Deactivate
              </button>
            </div>
          )}
        </div>

        {actionError && (
          <div style={{ color: '#dc2626', fontSize: '13px', marginBottom: '12px' }}>
            {actionError}
          </div>
        )}
      </div>

      {/* Tab navigation */}
      <div style={{ borderBottom: '1px solid var(--color-light-grey)', padding: '0 24px' }}>
        <div style={{ display: 'flex', gap: '24px' }}>
          {(['overview', 'projects', 'audit'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '12px 0',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                color: activeTab === tab ? 'var(--color-magenta)' : 'var(--color-dark-grey)',
                borderBottom: activeTab === tab ? '2px solid var(--color-magenta)' : 'none',
                fontFamily: 'var(--font-family)',
                textTransform: 'capitalize',
              }}
            >
              {tab === 'projects' ? 'Associated projects' : tab === 'audit' ? 'Audit history' : 'Overview'}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div style={{ padding: '24px' }}>
        {activeTab === 'overview' && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
            }}
          >
            {[
              { label: 'Domain', value: company.domain || '-' },
              { label: 'Source system', value: company.sourceSystem || '-' },
              { label: 'Status', value: statusLabel(company.isActive) },
              // Bug #331: these read `company.created` / `company.updated`,
              // neither of which exists on the API payload or the type. The
              // mock was supplying nothing for them either, so both showed
              // the formatter's placeholder.
              { label: 'Created', value: formatDate(company.createdAt) },
              { label: 'Last updated', value: formatDate(company.updatedAt) },
            ].map((field) => (
              <div
                key={field.label}
                style={{
                  padding: '12px',
                  backgroundColor: 'var(--color-white)',
                  borderRadius: '6px',
                  border: '1px solid var(--color-light-grey)',
                }}
              >
                <div
                  style={{
                    fontSize: '12px',
                    color: 'var(--color-dark-grey)',
                    marginBottom: '4px',
                    fontWeight: 500,
                  }}
                >
                  {field.label}
                </div>
                <div style={{ fontSize: '14px', color: 'var(--color-black)', fontWeight: 500 }}>
                  {field.value}
                </div>
              </div>
            ))}

            {company.sourceUrl && (
              <a
                href={company.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '12px',
                  backgroundColor: 'var(--color-white)',
                  borderRadius: '6px',
                  border: '1px solid var(--color-light-grey)',
                  textDecoration: 'none',
                  color: 'var(--color-magenta)',
                  fontWeight: 500,
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                View in source system
              </a>
            )}
          </div>
        )}

        {activeTab === 'projects' && (
          <div>
            {projectsLoading ? (
              <div
                style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-dark-grey)' }}
              >
                Loading projects...
              </div>
            ) : projectsError ? (
              <div style={{ color: '#dc2626', fontSize: '14px' }}>{projectsError}</div>
            ) : projects.length === 0 ? (
              <div
                style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-dark-grey)' }}
              >
                No projects associated with this company.
              </div>
            ) : (
              <DataTable
                columns={[
                  {
                    key: 'name',
                    header: 'Name',
                    render: (value: string) => <div style={{ fontWeight: 500 }}>{value}</div>,
                  },
                  {
                    key: 'status',
                    header: 'Status',
                    // Status arrives lowercase from the backend, the same
                    // trap ProjectList hit under #328.
                    render: (value: string) => (
                      <span style={badgeStyle(isActiveStatus(value))}>{titleCase(value)}</span>
                    ),
                  },
                  { key: 'sourceSystem', header: 'Source', render: (value?: string) => value || '-' },
                  { key: 'createdAt', header: 'Created', render: (value?: string) => formatDate(value) },
                ]}
                data={projects}
                rowKey="id"
                onRowClick={(row: Project) => navigate(`/projects/${row.id}`)}
                actions={[
                  { label: 'View', onClick: (row: Project) => navigate(`/projects/${row.id}`) },
                ]}
              />
            )}
          </div>
        )}

        {activeTab === 'audit' && (
          <AuditHistoryTab entityId={company.id} entityLabel="company" />
        )}
      </div>

      {/* Edit modal */}
      <Modal
        isOpen={editModalOpen}
        title="Edit company"
        onClose={() => setEditModalOpen(false)}
        isDirty={
          editData.name !== company.name ||
          editData.domain !== company.domain ||
          editData.sourceUrl !== company.sourceUrl
        }
        footer={
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setEditModalOpen(false)}
              style={{
                padding: '8px 16px',
                border: '1px solid var(--color-light-grey)',
                borderRadius: '6px',
                backgroundColor: 'var(--color-white)',
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !editData.name}
              style={{
                padding: '8px 16px',
                backgroundColor: 'var(--color-magenta)',
                color: 'var(--color-white)',
                border: 'none',
                borderRadius: '6px',
                cursor: saving || !editData.name ? 'not-allowed' : 'pointer',
                opacity: saving || !editData.name ? 0.6 : 1,
                fontFamily: 'var(--font-family)',
              }}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        }
      >
        <div style={{ display: 'grid', gap: '16px' }}>
          {[
            { label: 'Company name', key: 'name' as const },
            { label: 'Domain', key: 'domain' as const },
            { label: 'Source URL', key: 'sourceUrl' as const },
          ].map((field) => (
            <div key={field.key}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                {field.label}
              </label>
              <input
                type="text"
                value={String(editData[field.key] ?? '')}
                onChange={(e) => setEditData({ ...editData, [field.key]: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                }}
              />
            </div>
          ))}
        </div>
      </Modal>
    </div>
  )
}

export default CompanyDetail
